import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

class DriverBulkUploadService {
  /**
   * Parse file (CSV or Excel) and extract drivers data with 3-column mapping
   * @param {File} file - The file to parse (CSV or Excel)
   * @returns {Promise<Array>} Array of driver objects { phone, contactSource, city }
   */
  static async parseFileToDrivers(file) {
    const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
    
    if (isExcel) {
      return this.parseExcelFile(file);
    } else {
      return this.parseCsvFile(file);
    }
  }

  /**
   * Parse Excel file and extract drivers data
   * @param {File} file - Excel file
   * @returns {Promise<Array>} Array of driver objects
   */
  static async parseExcelFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          
          // Get first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON array
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
          
          if (jsonData.length < 2) {
            reject(new Error("Excel file must have at least a header row and one data row"));
            return;
          }
          
          // First row is headers
          const headers = jsonData[0].map(h => String(h).trim());
          
          // Find column indices (case-insensitive)
          const mobileIndex = headers.findIndex(h => 
            h.toLowerCase().includes("mobile") && h.toLowerCase().includes("number")
          );
          const contactSourceIndex = headers.findIndex(h => 
            h.toLowerCase().includes("contact") && h.toLowerCase().includes("source")
          );
          const cityIndex = headers.findIndex(h => 
            h.toLowerCase() === "city"
          );
          
          if (mobileIndex === -1) {
            reject(new Error("Column 'Mobile Number' not found in Excel file"));
            return;
          }
          
          // Map data rows
          const drivers = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            const phone = row[mobileIndex] ? String(row[mobileIndex]).trim() : "";
            const contactSource = contactSourceIndex !== -1 && row[contactSourceIndex] 
              ? String(row[contactSourceIndex]).trim() 
              : null;
            const city = cityIndex !== -1 && row[cityIndex] 
              ? String(row[cityIndex]).trim() 
              : null;
            
            // Skip empty rows
            if (!phone) continue;
            
            drivers.push({
              phone,
              contactSource: contactSource || null,
              city: city || null,
            });
          }
          
          resolve(drivers);
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error.message}`));
        }
      };
      
      reader.onerror = () => reject(new Error("Failed to read Excel file"));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Parse CSV file and extract drivers data
   * @param {File} file - CSV file
   * @returns {Promise<Array>} Array of driver objects
   */
  static async parseCsvFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const csv = e.target.result;
          const lines = csv.split("\n").filter(line => line.trim());
          
          if (lines.length < 2) {
            reject(new Error("CSV file must have at least a header row and one data row"));
            return;
          }
          
          // Parse headers (handle quoted values)
          const parseCsvLine = (line) => {
            const result = [];
            let current = "";
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === "," && !inQuotes) {
                result.push(current.trim());
                current = "";
              } else {
                current += char;
              }
            }
            result.push(current.trim());
            return result;
          };
          
          const headers = parseCsvLine(lines[0]).map(h => h.replace(/^"|"$/g, "").trim());
          
          // Find column indices (case-insensitive)
          const mobileIndex = headers.findIndex(h => 
            h.toLowerCase().includes("mobile") && h.toLowerCase().includes("number")
          );
          const contactSourceIndex = headers.findIndex(h => 
            h.toLowerCase().includes("contact") && h.toLowerCase().includes("source")
          );
          const cityIndex = headers.findIndex(h => 
            h.toLowerCase() === "city"
          );
          
          if (mobileIndex === -1) {
            reject(new Error("Column 'Mobile Number' not found in CSV file"));
            return;
          }
          
          // Map data rows
          const drivers = [];
          for (let i = 1; i < lines.length; i++) {
            const row = parseCsvLine(lines[i]).map(cell => cell.replace(/^"|"$/g, "").trim());
            const phone = row[mobileIndex] || "";
            const contactSource = contactSourceIndex !== -1 && row[contactSourceIndex] 
              ? row[contactSourceIndex] 
              : null;
            const city = cityIndex !== -1 && row[cityIndex] 
              ? row[cityIndex] 
              : null;
            
            // Skip empty rows
            if (!phone) continue;
            
            drivers.push({
              phone,
              contactSource: contactSource || null,
              city: city || null,
            });
          }
          
          resolve(drivers);
        } catch (error) {
          reject(new Error(`Failed to parse CSV file: ${error.message}`));
        }
      };
      
      reader.onerror = () => reject(new Error("Failed to read CSV file"));
      reader.readAsText(file);
    });
  }

  /**
   * Upload drivers data for bulk driver creation
   * @param {File} file - The file to upload (CSV or Excel)
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise} API response
   */
  static async bulkUploadDrivers(file, onProgress = null) {
    let drivers = [];
    let phoneNumber = "N/A";
    
    try {
      // Parse file to get drivers array
      if (onProgress) onProgress(10);
      
      drivers = await this.parseFileToDrivers(file);
      
      if (drivers.length === 0) {
        throw new Error("No valid driver data found in file");
      }
      
      // Extract phone number for error reporting
      if (drivers && drivers.length > 0 && drivers[0].phone) {
        phoneNumber = drivers[0].phone;
      }
      
      if (onProgress) onProgress(30);
      
      // Log request details for debugging
      console.log("Bulk upload request:", {
        url: API.ADMIN.DRIVER_BULK_UPLOAD,
        driversCount: drivers.length,
        firstDriver: drivers[0] || null,
      });
      
      // Send JSON POST request
      const response = await axiosInstance.post(
        API.ADMIN.DRIVER_BULK_UPLOAD,
        { drivers },
        {
          headers: {
            "Content-Type": "application/json",
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              // Map from 30% to 100% (30% for parsing, 70% for upload)
              const uploadPercent = Math.round(
                (progressEvent.loaded * 70) / progressEvent.total
              );
              onProgress(30 + uploadPercent);
            }
          },
        }
      );

      console.log("Bulk upload response:", response.data);

      // Handle response structure
      let resultData = response.data;

      if (response.data.success && response.data.data) {
        resultData = {
          ...response.data.data,
          success: response.data.success,
          message: response.data.message,
        };
      } else if (
        response.data &&
        (response.data.total !== undefined ||
          response.data.successful !== undefined ||
          response.data.created !== undefined ||
          response.data.processed !== undefined)
      ) {
        resultData = {
          ...response.data,
          success: response.data.success !== false,
        };
      }

      // Ensure we have the required fields with fallbacks
      const processedResults = {
        successful:
          resultData.successful ||
          resultData.success_count ||
          resultData.created ||
          resultData.uploaded ||
          0,
        failed:
          resultData.failed ||
          resultData.error_count ||
          resultData.errors?.length ||
          0,
        totalProcessed:
          resultData.total ||
          resultData.totalProcessed ||
          resultData.processed ||
          (resultData.successful || resultData.created || 0) +
            (resultData.failed || resultData.errors?.length || 0),
        errors: resultData.errors || [],
        message: resultData.message || "",
        success: resultData.success !== false,
      };

      if (processedResults.successful > 0) {
        toast.success(
          `Bulk upload completed: ${processedResults.successful} drivers created successfully`
        );
      } else if (processedResults.failed > 0) {
        toast.error(
          `Upload failed: ${processedResults.failed} records had errors`
        );
      } else {
        toast.success("Upload completed successfully");
      }

      if (onProgress) onProgress(100);

      return {
        success: processedResults.success,
        data: processedResults,
        message: processedResults.message,
      };
    } catch (error) {
      // Detailed error logging for debugging
      console.error("Bulk upload error details:", {
        url: API.ADMIN.DRIVER_BULK_UPLOAD,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        code: error.code,
      });

      // Extract error message based on status code
      let errorMessage = "Failed to upload drivers. Please try again.";
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const responseData = error.response.data;
        
        if (status === 404) {
          errorMessage = "Backend server not found (404). Please ensure the backend server is running and accessible.";
        } else if (status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (status === 403) {
          errorMessage = "Access forbidden. You don't have permission to perform this action.";
        } else if (status === 500) {
          errorMessage = "Server error occurred. Please try again later.";
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        } else {
          errorMessage = `Request failed with status code ${status}`;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = "No response from server. Please check if the backend server is running.";
      } else {
        // Error in setting up the request
        errorMessage = error.message || "Failed to upload drivers. Please try again.";
      }

      // Phone number already extracted above, use it here

      toast.error(errorMessage);

      return {
        success: false,
        data: {
          successful: 0,
          failed: 1,
          totalProcessed: 1,
          errors: [
            {
              row: 1,
              error: errorMessage,
              phone: phoneNumber,
            },
          ],
          message: errorMessage,
        },
        message: errorMessage,
      };
    }
  }

  /**
   * Download CSV template
   * @returns {Promise} CSV template blob
   */
  static async downloadCsvTemplate() {
    try {
      // First try to get template from server
      try {
        const response = await axiosInstance.get(
          `${API.ADMIN.DRIVER_BULK_UPLOAD}/template`,
          {
            responseType: "blob",
          }
        );

        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "driver_bulk_upload_template.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("CSV template downloaded successfully");
        return response.data;
      } catch (serverError) {
        // If server template fails, create client-side template
        console.log(
          "Server template not available, creating client-side template"
        );
        this.createSampleTemplate();
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download CSV template");
      throw error;
    }
  }

  /**
   * Create and download sample template locally
   */
  static createSampleTemplate() {
    const csvContent = this.createSampleCsvContent();
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "driver_bulk_upload_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("CSV template downloaded successfully");
  }

  /**
   * Get bulk upload status/statistics
   * @returns {Promise} Upload statistics
   */
  static async getBulkUploadStatus() {
    try {
      const response = await axiosInstance.get(
        `${API.ADMIN.DRIVER_BULK_UPLOAD}/status`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching bulk upload status:", error);
      throw error;
    }
  }

  /**
   * Validate file before upload (CSV or Excel)
   * @param {File} file - File to validate
   * @returns {Object} Validation result
   */
  static validateFile(file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedExtensions = [".csv", ".xlsx", ".xls"];

    if (!file) {
      return { valid: false, error: "Please select a file" };
    }

    if (file.size > maxSize) {
      return { valid: false, error: "File size must be less than 10MB" };
    }

    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      return { valid: false, error: "Please upload a valid CSV or Excel file (.csv, .xlsx, .xls)" };
    }

    return { valid: true };
  }

  /**
   * Get file preview data (first few rows) for CSV or Excel
   * @param {File} file - File to preview
   * @param {number} maxRows - Maximum rows to preview
   * @returns {Promise} Preview data with mapped columns
   */
  static async getFilePreview(file, maxRows = 5) {
    const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
    
    if (isExcel) {
      return this.getExcelPreview(file, maxRows);
    } else {
      return this.getCsvPreview(file, maxRows);
    }
  }

  /**
   * Get Excel preview
   * @param {File} file - Excel file
   * @param {number} maxRows - Maximum rows to preview
   * @returns {Promise} Preview data
   */
  static async getExcelPreview(file, maxRows = 5) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
          
          if (jsonData.length < 1) {
            reject(new Error("Excel file is empty"));
            return;
          }
          
          const headers = jsonData[0].map(h => String(h).trim());
          
          // Find column indices
          const mobileIndex = headers.findIndex(h => 
            h.toLowerCase().includes("mobile") && h.toLowerCase().includes("number")
          );
          const contactSourceIndex = headers.findIndex(h => 
            h.toLowerCase().includes("contact") && h.toLowerCase().includes("source")
          );
          const cityIndex = headers.findIndex(h => 
            h.toLowerCase() === "city"
          );
          
          // Map to our 3-column format
          const mappedHeaders = ["Mobile Number", "Contact Source", "City"];
          const mappedRows = [];
          
          for (let i = 1; i < Math.min(jsonData.length, maxRows + 1); i++) {
            const row = jsonData[i];
            const mappedRow = [
              mobileIndex !== -1 ? String(row[mobileIndex] || "").trim() : "",
              contactSourceIndex !== -1 ? String(row[contactSourceIndex] || "").trim() : "",
              cityIndex !== -1 ? String(row[cityIndex] || "").trim() : "",
            ];
            mappedRows.push(mappedRow);
          }
          
          resolve({
            headers: mappedHeaders,
            rows: mappedRows,
            totalRows: jsonData.length - 1,
          });
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error.message}`));
        }
      };
      
      reader.onerror = () => reject(new Error("Failed to read Excel file"));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Parse CSV preview data (first few rows) with 3-column mapping
   * @param {File} file - CSV file to preview
   * @param {number} maxRows - Maximum rows to preview
   * @returns {Promise} Preview data
   */
  static async getCsvPreview(file, maxRows = 5) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const csv = e.target.result;
          const lines = csv.split("\n").filter(line => line.trim());
          
          if (lines.length < 1) {
            reject(new Error("CSV file is empty"));
            return;
          }
          
          // Parse CSV line (handle quoted values)
          const parseCsvLine = (line) => {
            const result = [];
            let current = "";
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === "," && !inQuotes) {
                result.push(current.trim());
                current = "";
              } else {
                current += char;
              }
            }
            result.push(current.trim());
            return result;
          };
          
          const headers = parseCsvLine(lines[0]).map(h => h.replace(/^"|"$/g, "").trim());
          
          // Find column indices
          const mobileIndex = headers.findIndex(h => 
            h.toLowerCase().includes("mobile") && h.toLowerCase().includes("number")
          );
          const contactSourceIndex = headers.findIndex(h => 
            h.toLowerCase().includes("contact") && h.toLowerCase().includes("source")
          );
          const cityIndex = headers.findIndex(h => 
            h.toLowerCase() === "city"
          );
          
          // Map to our 3-column format
          const mappedHeaders = ["Mobile Number", "Contact Source", "City"];
          const mappedRows = [];
          
          for (let i = 1; i < Math.min(lines.length, maxRows + 1); i++) {
            const row = parseCsvLine(lines[i]).map(cell => cell.replace(/^"|"$/g, "").trim());
            const mappedRow = [
              mobileIndex !== -1 ? (row[mobileIndex] || "") : "",
              contactSourceIndex !== -1 ? (row[contactSourceIndex] || "") : "",
              cityIndex !== -1 ? (row[cityIndex] || "") : "",
            ];
            mappedRows.push(mappedRow);
          }

          resolve({
            headers: mappedHeaders,
            rows: mappedRows,
            totalRows: lines.length - 1,
          });
        } catch (error) {
          reject(new Error("Failed to parse CSV file"));
        }
      };

      reader.onerror = () => reject(new Error("Failed to read CSV file"));
      reader.readAsText(file);
    });
  }

  /**
   * Create sample CSV content for template (3 columns only)
   * @returns {string} CSV content
   */
  static createSampleCsvContent() {
    const headers = ["Mobile Number", "Contact Source", "City"];
    const sampleData = [
      ["1111111111", "WhatsApp Group", "Hyderabad"],
    ];

    const csvContent = [
      headers.join(","),
      ...sampleData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    return csvContent;
  }
}

export default DriverBulkUploadService;
